/**
 * DataSourceWizardPage — Dynamic multi-step wizard for creating data sources.
 *
 * Orchestrates all wizard state and delegates each step to its sub-component.
 * Step flow is driven by the provider manifest's wizard_steps array.
 */

import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@/i18n';
import { toast } from 'sonner';
import { useCreateDataSource, useBindDataSource } from '../hooks/use-datasources';
import { WizardLayout } from '../components/wizard/WizardLayout';
import { StepProviderSelect } from '../components/wizard/StepProviderSelect';
import { StepConnect } from '../components/wizard/StepConnect';
import { StepBrowse } from '../components/wizard/StepBrowse';
import { StepConfigure } from '../components/wizard/StepConfigure';
import { StepReview } from '../components/wizard/StepReview';
import type {
  WizardStep,
  EnrichedProviderManifest,
  FilterConfig,
  SyncConfig,
  DataSourceCreate,
} from '../types';
import type { BrowseSelection } from '../components/browse/types';

// =============================================================================
// Wizard State
// =============================================================================

interface WizardState {
  selectedManifest: EnrichedProviderManifest | null;
  credentialToken: string | null;
  installationId: number | null;
  credentials: Record<string, string>;
  browseSelection: BrowseSelection | null;
  name: string;
  providerConfig: Record<string, unknown>;
  filterConfig: Record<string, unknown>;
  syncConfig: Record<string, unknown>;
  collectionId: string | null;
}

const INITIAL_STATE: WizardState = {
  selectedManifest: null,
  credentialToken: null,
  installationId: null,
  credentials: {},
  browseSelection: null,
  name: '',
  providerConfig: {},
  filterConfig: {},
  syncConfig: {},
  collectionId: null,
};

// =============================================================================
// DataSourceWizardPage
// =============================================================================

export function DataSourceWizardPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const createDS = useCreateDataSource();
  const bindDS = useBindDataSource();

  const [state, setState] = useState<WizardState>(INITIAL_STATE);
  const [stepIndex, setStepIndex] = useState(-1); // -1 = provider select (pre-wizard)

  // Dynamic step list from manifest
  const steps: WizardStep[] = useMemo(
    () => state.selectedManifest?.wizard_steps ?? [],
    [state.selectedManifest],
  );

  // ----- state updaters ------
  const update = useCallback(
    <K extends keyof WizardState>(key: K, value: WizardState[K]) =>
      setState((prev) => ({ ...prev, [key]: value })),
    [],
  );

  // ----- provider selection ------
  const handleProviderSelect = useCallback(
    (manifest: EnrichedProviderManifest) => {
      setState({
        ...INITIAL_STATE,
        selectedManifest: manifest,
        filterConfig: manifest.default_filter_config ?? {},
        syncConfig: manifest.default_sync_config ?? {},
        providerConfig: manifest.config_defaults ?? {},
        name: '',
      });
      setStepIndex(0);
    },
    [],
  );

  // ----- navigation ------
  const handleBack = useCallback(() => {
    if (stepIndex <= 0) {
      setState(INITIAL_STATE);
      setStepIndex(-1);
    } else {
      setStepIndex((i) => i - 1);
    }
  }, [stepIndex]);

  const handleNext = useCallback(() => {
    setStepIndex((i) => Math.min(i + 1, steps.length - 1));
  }, [steps.length]);

  // ----- submission ------
  const handleSubmit = useCallback(async () => {
    const m = state.selectedManifest;
    if (!m) return;

    const payload: DataSourceCreate = {
      name: state.name || m.name_template,
      provider_type: m.provider_type as DataSourceCreate['provider_type'],
      provider_config: state.providerConfig,
      filter_config: Object.keys(state.filterConfig).length > 0 ? state.filterConfig as unknown as FilterConfig : undefined,
      sync_config: Object.keys(state.syncConfig).length > 0 ? state.syncConfig as unknown as SyncConfig : undefined,
    };

    // Auth: credential token (OAuth) vs raw credentials
    if (state.credentialToken) {
      payload.oauth_credential_token = state.credentialToken;
    } else if (Object.keys(state.credentials).length > 0) {
      payload.credential = state.credentials;
    }

    // GitHub app_install: pass installation_id in provider_config
    if (state.installationId) {
      payload.provider_config = {
        ...payload.provider_config,
        installation_id: state.installationId,
      };
    }

    try {
      const ds = await createDS.mutateAsync(payload);

      // Bind to collection if selected
      if (state.collectionId) {
        try {
          await bindDS.mutateAsync({ id: ds.id, collection_id: state.collectionId });
        } catch (bindErr: unknown) {
          // ISS-030: Surface the specific backend reason so the user knows
          // why the binding failed (e.g., "Collection has N documents" or
          // "Collection already has a DataSource"). Datasource was created
          // successfully — user can retry binding from the detail page.
          const detail =
            (bindErr as { response?: { data?: { detail?: { message?: string } | string } } })
              ?.response?.data?.detail;
          const backendMsg =
            typeof detail === 'string'
              ? detail
              : detail?.message ?? (bindErr as Error)?.message ?? '';
          toast.warning(
            backendMsg
              ? `${t('datasources.wizard.review.bindFailed')}: ${backendMsg}`
              : t('datasources.wizard.review.bindFailed'),
          );
        }
      }

      toast.success(t('datasources.wizard.review.created'));
      navigate(`/knowledge/datasources/${ds.id}`);
    } catch {
      toast.error(t('datasources.wizard.review.createFailed'));
    }
  }, [state, createDS, bindDS, navigate, t]);

  // ----- can-advance logic per step ------
  const canAdvance = useMemo(() => {
    if (stepIndex < 0 || stepIndex >= steps.length) return false;
    const step = steps[stepIndex];

    switch (step) {
      case 'connect': {
        const m = state.selectedManifest;
        if (!m) return false;
        if (m.auth_method === 'none') return true;
        if (m.auth_method === 'oauth') return !!state.credentialToken;
        if (m.auth_method === 'app_install') return !!state.installationId;
        if (m.auth_method === 'credentials') {
          const required = m.credential_fields.filter((f) => f.required);
          return required.every((f) => !!state.credentials[f.key]);
        }
        return false;
      }
      case 'browse':
        return !!state.browseSelection;
      case 'configure':
        return !!state.name.trim();
      case 'review':
        return true; // can always submit from review
      default:
        return true;
    }
  }, [stepIndex, steps, state]);

  const isLastStep = stepIndex === steps.length - 1;
  const isSubmitting = createDS.isPending || bindDS.isPending;

  // ----- render provider select (pre-wizard) ------
  if (stepIndex === -1) {
    return (
      <StepProviderSelect
        onSelect={handleProviderSelect}
      />
    );
  }

  const manifest = state.selectedManifest!;
  const currentStep = steps[stepIndex];

  // ----- render wizard ------
  return (
    <WizardLayout
      steps={steps}
      currentStep={stepIndex}
      onBack={handleBack}
      onNext={isLastStep ? handleSubmit : handleNext}
      canAdvance={canAdvance}
      isSubmitting={isSubmitting}
    >
      {currentStep === 'connect' && (
        <StepConnect
          manifest={manifest}
          credentialToken={state.credentialToken}
          installationId={state.installationId}
          credentials={state.credentials}
          onCredentialToken={(token) => update('credentialToken', token)}
          onInstallationId={(id) => update('installationId', id)}
          onCredentials={(creds: Record<string, string>) => update('credentials', creds)}
        />
      )}

      {currentStep === 'browse' && (
        <StepBrowse
          manifest={manifest}
          credentialToken={state.credentialToken}
          installationId={state.installationId}
          onSelect={(sel) => {
            update('browseSelection', sel);
            // Auto-fill provider config from browse selection
            if (sel?.config) {
              setState((prev) => ({
                ...prev,
                providerConfig: { ...prev.providerConfig, ...sel.config },
              }));
            }
          }}
        />
      )}

      {currentStep === 'configure' && (
        <StepConfigure
          manifest={manifest}
          name={state.name}
          providerConfig={state.providerConfig}
          filterConfig={state.filterConfig}
          syncConfig={state.syncConfig}
          browseSelection={state.browseSelection}
          onNameChange={(n) => update('name', n)}
          onProviderConfigChange={(c) => update('providerConfig', c)}
          onFilterConfigChange={(f) => update('filterConfig', f)}
          onSyncConfigChange={(s) => update('syncConfig', s)}
        />
      )}

      {currentStep === 'review' && (
        <StepReview
          manifest={manifest}
          name={state.name}
          browseSelection={state.browseSelection}
          providerConfig={state.providerConfig}
          syncConfig={state.syncConfig}
          collectionId={state.collectionId}
          onCollectionChange={(id) => update('collectionId', id)}
        />
      )}
    </WizardLayout>
  );
}
